import { supabase } from '@supabaseClient/supabaseClient';
import { IBackorderQueue } from './OrdersClient';

export interface ICreateBackorderQueue {
  order_id: string;
  order_item_id: string;
  product_id: string;
  quantity_needed: number;
  priority: number;
  organization_id: string;
}

export interface IUpdateBackorderQueue {
  id: string;
  quantity_needed?: number;
  priority?: number;
  status?: 'pending' | 'in_progress' | 'fulfilled' | 'cancelled';
}

export class BackorderQueueClient {
  /**
   * Create a new backorder queue entry
   */
  static async createBackorderQueue(
    entry: ICreateBackorderQueue
  ): Promise<IBackorderQueue> {
    const { data, error } = await supabase
      .from('backorder_queue')
      .insert(entry)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return data;
  }

  /**
   * Get all backorder queue entries for a product
   */
  static async getBackorderQueueByProductId(
    productId: string
  ): Promise<IBackorderQueue[]> {
    const { data, error } = await supabase
      .from('backorder_queue')
      .select('*')
      .eq('product_id', productId)
      .eq('status', 'pending') // Only get pending backorders
      .order('priority', { ascending: true })
      .order('created_at', { ascending: true });

    if (error) {
      throw error;
    }

    return data || [];
  }

  /**
   * Get backorder queue entries by order item ID
   */
  static async getBackorderQueueByOrderItemId(
    orderItemId: string
  ): Promise<IBackorderQueue[]> {
    const { data, error } = await supabase
      .from('backorder_queue')
      .select('*')
      .eq('order_item_id', orderItemId)
      .order('created_at', { ascending: true });

    if (error) {
      throw error;
    }

    return data || [];
  }

  /**
   * Get all pending backorder queue entries for an organization
   */
  static async getPendingBackorderQueue(
    organizationId?: string
  ): Promise<IBackorderQueue[]> {
    let query = supabase
      .from('backorder_queue')
      .select('*')
      .eq('status', 'pending')
      .order('priority', { ascending: true })
      .order('created_at', { ascending: true });

    if (organizationId) {
      query = query.eq('organization_id', organizationId);
    }

    const { data, error } = await query;

    if (error) {
      throw error;
    }

    return data || [];
  }

  /**
   * Update a backorder queue entry
   */
  static async updateBackorderQueue(
    entry: IUpdateBackorderQueue
  ): Promise<IBackorderQueue> {
    const { id, ...updateData } = entry;

    const { data, error } = await supabase
      .from('backorder_queue')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return data;
  }

  /**
   * Delete a backorder queue entry
   */
  static async deleteBackorderQueue(id: string): Promise<void> {
    const { error } = await supabase
      .from('backorder_queue')
      .delete()
      .eq('id', id);

    if (error) {
      throw error;
    }
  }

  /**
   * Delete all backorder queue entries for an order
   */
  static async deleteBackorderQueueByOrderId(orderId: string): Promise<void> {
    const { error } = await supabase
      .from('backorder_queue')
      .delete()
      .eq('order_id', orderId);

    if (error) {
      throw error;
    }
  }

  /**
   * Delete all backorder queue entries for an order item
   */
  static async deleteBackorderQueueByOrderItemId(
    orderItemId: string
  ): Promise<void> {
    const { error } = await supabase
      .from('backorder_queue')
      .delete()
      .eq('order_item_id', orderItemId);

    if (error) {
      throw error;
    }
  }
}
